import React, { useState, useMemo, useRef } from 'react'
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  TextField,
  Button,
  Tabs,
  Tab,
  IconButton,
  Popover,
} from '@mui/material'
import LockIcon from '@mui/icons-material/Lock'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import axios from 'axios'
import useRouter from '../hooks/useRouter'
import useAccount from '../hooks/useAccount'
import useSharedDashboard from '../hooks/useSharedDashboard'
import useDashboards from '../hooks/useDashboards'
import DashboardLayoutComponent from '../components/dashboard/DashboardLayoutComponent'
import { DashboardPage, BrandPaletteEntry, WebappDashboardConfig, VideoDashboardConfig } from '../types/gotypes'
import { resolvePageColorMap, themeColorMapsFromResolved, themeLookupFromResolved } from '../utils/brandTheme'
import { SharedDashboardContext } from '../contexts/SharedDashboardContext'
import { API_BASE_URL } from '../constants/system'
import { isWebappBundle } from '../utils/dashboardType'
import VideoDashboardView from '../components/dashboard/VideoDashboardView'
import usePublishedPresentation from '../hooks/usePublishedPresentation'

const SharedDashboardView: React.FC = () => {
  const router = useRouter()
  const account = useAccount()
  const shareId = router.params.shareId || ''

  const {
    data,
    dashboard,
    isLoading,
    isError,
    verifyPassword,
    verifying,
    verifyError,
  } = useSharedDashboard(shareId)

  const [password, setPassword] = useState('')

  // For customer-restricted dashboards, use the authenticated endpoint
  const customerDashboard = useDashboards({
    customer: data?.customer || '',
    dashboardId: data?.dashboard_id,
  })

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    verifyPassword(password)
  }

  const handleLoginClick = () => {
    account.setPostLoginRoute({ name: 'shared-dashboard', params: { shareId } })
    router.navigate('home')
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (isError || !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Alert severity="error">Dashboard not found or sharing has been disabled.</Alert>
      </Box>
    )
  }

  // A web app bundle (a Web app or a Presentation), at any access level
  const isWebapp = isWebappBundle(data.type) || isWebappBundle(dashboard?.type)
  const isVideo = data.type === 'video' || dashboard?.type === 'video'

  // Password-protected: show password form until verified
  if (data.access_level === 'password' && !dashboard) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: 'grey.50' }}>
        <Paper sx={{ p: 4, maxWidth: 400, width: '100%' }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <LockIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6">{data.name || 'Protected Dashboard'}</Typography>
            <Typography variant="body2" color="text.secondary">
              This dashboard is password protected.
            </Typography>
          </Box>
          <form onSubmit={handlePasswordSubmit}>
            <TextField
              fullWidth
              type="password"
              label="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={Boolean(verifyError)}
              helperText={verifyError}
              sx={{ mb: 2 }}
              autoFocus
            />
            <Button
              fullWidth
              type="submit"
              variant="contained"
              color="info"
              disabled={!password || verifying}
              startIcon={verifying ? <CircularProgress size={16} /> : undefined}
            >
              {verifying ? 'Verifying...' : 'View Dashboard'}
            </Button>
          </form>
        </Paper>
      </Box>
    )
  }

  // Customer-restricted: prompt login or use authenticated endpoint
  if (data.access_level === 'customer') {
    // Wait for auth to initialise before deciding — avoids flash of login prompt for already-logged-in users
    if (!account.initialized) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
        </Box>
      )
    }

    if (!account.user) {
      // Auto-redirect to login with return route — no button required
      handleLoginClick()
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
        </Box>
      )
    }

    // User is logged in, use the authenticated dashboard
    const authDashboard = customerDashboard.dashboard
    if (customerDashboard.dashboardIsLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
        </Box>
      )
    }

    if (!authDashboard) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <Alert severity="error">You do not have access to this dashboard.</Alert>
        </Box>
      )
    }

    if (isWebappBundle(authDashboard.type) && authDashboard.config?.webapp_config) {
      return <SharedWebappView shareId="" config={authDashboard.config.webapp_config} authenticated={{ customer: authDashboard.customer, dashboardId: authDashboard.id }} source={{ customer: authDashboard.customer, dashboardId: authDashboard.id, name: authDashboard.config?.name }} />
    }

    if (authDashboard.type === 'video' && authDashboard.config?.video_config) {
      return <SharedVideoView name={authDashboard.config.name} description={authDashboard.config.description} shareId="" config={authDashboard.config.video_config} authenticated={{ customer: authDashboard.customer, dashboardId: authDashboard.id }} />
    }

    return (
      <SharedDashboardContent name={authDashboard.config?.name} description={authDashboard.config?.description} pages={authDashboard.config?.pages} layout={authDashboard.config?.layout} customer={authDashboard.customer} brandPalette={authDashboard.config?.brand_palette} resolvedThemes={authDashboard.resolved_themes} updatedAt={authDashboard.updated_at} isLoggedOut={false} />
    )
  }

  // Public or verified password: render the dashboard
  if (dashboard) {
    if (isWebapp && dashboard.config?.webapp_config) {
      return <SharedWebappView shareId={shareId} config={dashboard.config.webapp_config} source={{ customer: dashboard.customer, dashboardId: dashboard.id, name: dashboard.config?.name }} />
    }

    if (isVideo && dashboard.config?.video_config) {
      return <SharedVideoView name={dashboard.config.name} description={dashboard.config.description} shareId={shareId} config={dashboard.config.video_config} />
    }

    return (
      <SharedDashboardContext.Provider value={shareId}>
        <SharedDashboardContent name={dashboard.config?.name} description={dashboard.config?.description} pages={dashboard.config?.pages} layout={dashboard.config?.layout} customer={dashboard.customer} brandPalette={dashboard.config?.brand_palette} resolvedThemes={dashboard.resolved_themes} updatedAt={dashboard.updated_at} isLoggedOut={!account.user} />
      </SharedDashboardContext.Provider>
    )
  }

  return null
}

export function SharedWebappView({ shareId, config, authenticated, source }: {
  shareId: string
  config: WebappDashboardConfig
  authenticated?: { customer: string; dashboardId: string }
  /** The dashboard a presentation's Ask starts a chat from. Exported for its test. */
  source: { customer: string; dashboardId: string; name?: string }
}) {
  const account = useAccount()
  // For authenticated (customer access level), embed JWT in URL path so relative
  // sub-resources (JS/CSS) automatically carry the token.
  // For shared (public/password), use the shared endpoint (no auth needed).
  const authHeader = axios.defaults.headers.common['Authorization'] as string | undefined
  const authToken = authHeader?.replace('Bearer ', '')
  const src = authenticated && authToken
    ? `${API_BASE_URL}/dashboard-webapp/${authenticated.customer}/${authenticated.dashboardId}/${authToken}/${config.entry_point}`
    : `${API_BASE_URL}/shared/dashboard/${shareId}/webapp/${config.entry_point}`

  const frameRef = useRef<HTMLIFrameElement>(null)
  // A presentation (presentations): ?at= opens it at a slide. The deck's own
  // Ask is off here, and a share link shows no actions button: its Download
  // PDF printed badly and a shared viewer could not chat from it (Adam,
  // 5 Nov 2026). The round button was PageActionsFab; git history has it.
  const presentation = usePublishedPresentation({
    frameRef,
    customer: source.customer,
    dashboardId: source.dashboardId,
    name: source.name,
    job: config.job,
    signedIn: !!account.user,
    inCustomer: !!authenticated || !!account.user?.session_info?.sessionCusts?.some(c => c?.name === source.customer),
    deckAsk: false,
  })
  return (
    <Box sx={{ width: '100vw', height: '100vh', position: 'relative' }}>
      <iframe
        ref={frameRef}
        src={`${src}${presentation.initialHash}`}
        sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation allow-modals"
        style={{ width: '100%', height: '100%', border: 'none' }}
        title="Web app"
      />
    </Box>
  )
}

export function SharedVideoView({ name, description, shareId, config, authenticated }: {
  name?: string
  description?: string
  shareId: string
  config: VideoDashboardConfig
  authenticated?: { customer: string; dashboardId: string }
}) {
  // Exported for its test. A <video> cannot send a bearer header: the customer-level link carries the
  // JWT in the query, a public or password link uses the shared endpoint.
  const authHeader = axios.defaults.headers.common['Authorization'] as string | undefined
  const authToken = authHeader?.replace('Bearer ', '')
  const base = authenticated
    ? `${API_BASE_URL}/dashboards/${authenticated.customer}/${authenticated.dashboardId}/video`
    : `${API_BASE_URL}/shared/dashboard/${shareId}/video`
  const query = authenticated && authToken ? `?token=${authToken}` : ''
  return (
    <VideoDashboardView
      name={name || 'Video'}
      description={description}
      src={`${base}${query}`}
      downloadHref={`${base}${query ? `${query}&` : '?'}download=1`}
      fileName={config.file_name}
    />
  )
}

/** Format a dashboard updated_at (unix seconds or millis) for display. */
function formatLastUpdated(updatedAt?: number): string | null {
  if (!updatedAt) return null
  const ms = updatedAt < 1e12 ? updatedAt * 1000 : updatedAt
  return new Date(ms).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

const LOGIN_PROMPT = 'Log into Platinum for live filtering and agentic insights'

function SharedDashboardContent({ name, description, pages, layout, customer, brandPalette, resolvedThemes, updatedAt, isLoggedOut }: {
  name?: string
  description?: string
  pages?: DashboardPage[]
  layout?: { rows: any[] }
  customer?: string
  brandPalette?: Record<string, BrandPaletteEntry>
  /** Theme id → palette, resolved server-side (#1182) — the logged-out viewer's only theme source. */
  resolvedThemes?: Record<string, Record<string, BrandPaletteEntry>>
  updatedAt?: number
  isLoggedOut?: boolean
}) {
  const [activePageIndex, setActivePageIndex] = useState(0)
  const [infoAnchor, setInfoAnchor] = useState<HTMLElement | null>(null)

  const pageList = pages || []
  const activePage = pageList[activePageIndex] || pageList[0]
  // Per-PAGE colours (#1182): the page's theme reference over the snapshotted
  // job palette.
  const colorMap = useMemo(
    () => resolvePageColorMap(activePage, themeLookupFromResolved(resolvedThemes), brandPalette),
    [activePage, resolvedThemes, brandPalette]
  )
  // Chart-level themes (#1193), from the server-embedded resolved map.
  const themeColorMaps = useMemo(() => themeColorMapsFromResolved(resolvedThemes), [resolvedThemes])
  const activeLayout = activePage?.layout || layout || { rows: [] }
  const rows = activeLayout.rows || []
  const lastUpdated = formatLastUpdated(updatedAt)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Slim meta bar (#962 E) — the user's designed content leads; the
          internal dashboard name lives behind the info icon's popover. */}
      <Box
        sx={{
          px: 1.5,
          py: 0.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 1,
          backgroundColor: 'white',
          borderBottom: '1px solid',
          borderColor: 'grey.200',
        }}
      >
        {/* Understated status line for logged-out viewers (#962 D) */}
        {isLoggedOut && (
          <Typography variant="caption" sx={{ color: 'success.main' }}>
            {lastUpdated ? `Last updated: ${lastUpdated} · ` : ''}{LOGIN_PROMPT}
          </Typography>
        )}
        <IconButton
          size="small"
          aria-label="Dashboard information"
          onClick={(e) => setInfoAnchor(e.currentTarget)}
          sx={{ color: 'text.secondary' }}
        >
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
        <Popover
          open={Boolean(infoAnchor)}
          anchorEl={infoAnchor}
          onClose={() => setInfoAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box sx={{ p: 2, maxWidth: 320 }}>
            <Typography variant="subtitle2">{name || 'Dashboard'}</Typography>
            {description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {description}
              </Typography>
            )}
            {lastUpdated && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Last updated: {lastUpdated}
              </Typography>
            )}
            {isLoggedOut && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'success.main' }}>
                {LOGIN_PROMPT}
              </Typography>
            )}
          </Box>
        </Popover>
      </Box>

      {/* Page tabs - only show when 2+ pages */}
      {pageList.length > 1 && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', backgroundColor: 'white', display: 'flex', justifyContent: 'center' }}>
          <Tabs
            value={activePageIndex}
            onChange={(_e, newValue) => setActivePageIndex(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5, textTransform: 'none' } }}
          >
            {pageList.map((page) => (
              <Tab key={page.id} label={page.name} />
            ))}
          </Tabs>
        </Box>
      )}

      {/* Content */}
      <Box sx={{ flex: 1, p: 1.5, backgroundColor: 'grey.50' }}>
        {rows.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              backgroundColor: 'white',
              border: '1px dashed',
              borderColor: 'grey.300',
            }}
          >
            <Typography variant="body1" color="text.secondary">
              This dashboard is empty.
            </Typography>
          </Paper>
        ) : (
          <Paper elevation={1} sx={{ p: 1 }}>
            <DashboardLayoutComponent layout={activeLayout} customer={customer || ''} job="" readOnly={true} canRegenerate={false} colorMap={colorMap} themeColorMaps={themeColorMaps} dashboardConfig={{ name: name || '', description: description || '', layout: layout || { rows: [] }, pages }} currentPageIndex={activePageIndex} />
          </Paper>
        )}
      </Box>
    </Box>
  )
}

export default SharedDashboardView
